/**
 * Service for managing podcast episodes
 * Migrated to use HTTP API instead of direct Firestore calls
 */

import { api } from './api';
import {
  PodcastEpisode,
  EpisodeStatus,
  CreateEpisodeInput,
  UpdateEpisodeInput,
  EpisodeQueryOptions,
  CompanyEpisodeStats,
  PodcastChapter,
  PaginationMeta,
  PaginatedEpisodesResponse,
  MergePreview,
} from '../types/episode.types';
import {
  Clip,
  ClipStats,
  CreateClipEpisodeInput,
  CreateClipEpisodeResponse,
  UpdateClipInput,
} from '../types/clip.types';

/**
 * Podcast audio settings for episode merging
 */
export interface PodcastAudioSettings {
  introPath: string;
  outroPath: string;
  commercial1Path: string;
  commercial2Path: string;
  commercial3Path: string;
  commercial4Path: string;
  artworkPath: string;
  // Durations in seconds (fetched from audio metadata by API)
  introDuration?: number | null;
  outroDuration?: number | null;
  commercial1Duration?: number | null;
  commercial2Duration?: number | null;
  commercial3Duration?: number | null;
  commercial4Duration?: number | null;
  // Episode settings
  maxArticlesPerEpisode?: number;
}

/**
 * Service for managing podcast episodes via the standalone API
 */
export class PodcastEpisodeService {
  private static readonly BASE_PATH = '/podcast/episodes';

  // ==========================================
  // QUERY METHODS
  // ==========================================

  /**
   * Get all episodes for current company (legacy method - returns just episodes array)
   * @deprecated Use getEpisodesPaginated for pagination support
   */
  static async getEpisodes(options?: EpisodeQueryOptions): Promise<PodcastEpisode[]> {
    const result = await this.getEpisodesPaginated(options);
    return result.episodes;
  }

  /**
   * Get episodes with pagination support
   * Returns both episodes and pagination metadata
   */
  static async getEpisodesPaginated(options?: EpisodeQueryOptions): Promise<PaginatedEpisodesResponse> {
    const params: Record<string, unknown> = {};

    if (options?.status) {
      // Support both single status and array of statuses (comma-separated for API)
      params.status = Array.isArray(options.status)
        ? options.status.join(',')
        : options.status;
    }
    if (options?.orderBy) {
      params.sortBy = options.orderBy; // API uses sortBy, not orderBy
    }
    if (options?.order) {
      params.sortOrder = options.order; // API uses sortOrder, not order
    }
    if (options?.limit) {
      params.limit = options.limit;
    }
    if (options?.page) {
      params.page = options.page;
    }
    if (options?.search) {
      params.search = options.search;
    }
    if (options?.companyIds && options.companyIds.length > 0) {
      params.companyIds = options.companyIds.join(',');
    }

    // API returns paginated response with data array and meta object
    const response = await api.get<Array<PodcastEpisode & { publishedUrl?: string }>>(
      this.BASE_PATH,
      params
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch episodes');
    }

    // Map API field name to frontend type for each episode
    const episodes = response.data.map((episode) => {
      if (episode.publishedUrl && !episode.publishingProviderUrl) {
        episode.publishingProviderUrl = episode.publishedUrl;
      }
      return episode;
    });

    // Extract pagination meta from response (with fallback defaults)
    const meta: PaginationMeta = {
      page: response.meta?.page ?? options?.page ?? 1,
      limit: response.meta?.limit ?? options?.limit ?? 10,
      total: response.meta?.total ?? episodes.length,
      totalPages: response.meta?.totalPages ?? 1,
    };

    return { episodes, meta };
  }

  /**
   * Get episodes filtered by status
   */
  static async getEpisodesByStatus(status: EpisodeStatus): Promise<PodcastEpisode[]> {
    return this.getEpisodes({ status, orderBy: 'updatedAt', order: 'desc' });
  }

  /**
   * Get count of episodes with "audio_ready" status (for notification badge)
   */
  static async getReadyEpisodesCount(): Promise<number> {
    const readyEpisodes = await this.getEpisodesByStatus(EpisodeStatus.AUDIO_READY);
    return readyEpisodes.length;
  }

  /**
   * Get single episode by ID
   * @param episodeId The episode ID to fetch
   * @param companyIds Optional array of company IDs to search across (for multi-company users)
   */
  static async getEpisode(episodeId: string, companyIds?: string[]): Promise<PodcastEpisode | null> {
    try {
      // Build query params for multi-company lookup
      const params: Record<string, string> = {};
      if (companyIds && companyIds.length > 0) {
        params.companyIds = companyIds.join(',');
      }

      const response = await api.get<PodcastEpisode & { publishedUrl?: string }>(
        `${this.BASE_PATH}/${episodeId}`,
        params
      );

      if (!response.success) {
        if (response.error?.code === 'NOT_FOUND') {
          return null;
        }
        throw new Error(response.error?.message || 'Failed to fetch episode');
      }

      if (!response.data) {
        return null;
      }

      // Map API field name to frontend type
      const episode = response.data;
      if (episode.publishedUrl && !episode.publishingProviderUrl) {
        episode.publishingProviderUrl = episode.publishedUrl;
      }

      return episode;
    } catch (error: any) {
      // Handle 404 gracefully
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // ==========================================
  // CREATE METHODS
  // ==========================================

  /**
   * Create new draft episode from article
   */
  static async createEpisode(input: CreateEpisodeInput): Promise<string> {
    const response = await api.post<{ id: string }>(this.BASE_PATH, input);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create episode');
    }

    return response.data.id;
  }

  // ==========================================
  // UPDATE METHODS
  // ==========================================

  /**
   * Update episode metadata
   */
  static async updateEpisode(
    episodeId: string,
    updates: UpdateEpisodeInput
  ): Promise<void> {
    const response = await api.put(`${this.BASE_PATH}/${episodeId}`, updates);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update episode');
    }
  }

  /**
   * Update episode status
   */
  static async updateStatus(
    episodeId: string,
    newStatus: EpisodeStatus,
    additionalData?: Partial<PodcastEpisode>
  ): Promise<void> {
    const response = await api.patch(`${this.BASE_PATH}/${episodeId}/status`, {
      status: newStatus,
      ...additionalData,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update episode status');
    }
  }

  // ==========================================
  // WORKFLOW METHODS
  // ==========================================

  /**
   * Submit episode for audio generation
   */
  static async submitForProcessing(
    episodeId: string,
    introBumperId?: string | null,
    outroBumperId?: string | null
  ): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/process`, {
      introBumperId,
      outroBumperId,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to submit episode for processing');
    }
  }

  /**
   * Approve episode for publishing (legacy - uses Make.com webhook)
   * @deprecated Use publishToPodbean() instead for direct PodBean publishing
   */
  static async approveForPublishing(episodeId: string): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/approve`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to approve episode');
    }
  }

  /**
   * Publish episode directly to PodBean.
   * Returns immediately after triggering the publish; status updates come via WebSocket.
   *
   * @param episodeId - The episode ID to publish
   * @param chapters - Optional chapter data to save after publishing
   * @param status - Whether to publish live or as draft (default: 'publish')
   * @param generateArtwork - Whether to generate dated artwork before publishing (default: true)
   * @param publish_timestamp - Optional Unix timestamp for scheduled publishing (Podbean requires this field name)
   * @returns Promise with episodeId and initial status
   */
  static async publishToPodbean(
    episodeId: string,
    chapters?: PodcastChapter[],
    status: 'publish' | 'draft' | 'future' = 'publish',
    generateArtwork: boolean = true,
    publish_timestamp?: number
  ): Promise<{ episodeId: string; status: string; message: string }> {
    const response = await api.post<{ episodeId: string; status: string; message: string }>(
      `/episodes/${episodeId}/publish`,
      { chapters, status, generateArtwork, publish_timestamp }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to start publishing');
    }

    return response.data;
  }

  /**
   * Retry script generation for a failed episode
   */
  static async retryScript(episodeId: string): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/retry-script`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to retry script generation');
    }
  }

  // ==========================================
  // ARCHIVE & DELETE METHODS
  // ==========================================

  /**
   * Archive episode (required before deleting published episodes)
   */
  static async archiveEpisode(episodeId: string): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/archive`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to archive episode');
    }
  }

  /**
   * Unarchive episode (restores to draft status)
   */
  static async unarchiveEpisode(episodeId: string): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/unarchive`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to unarchive episode');
    }
  }

  /**
   * Delete episode (must be archived first if published)
   */
  static async deleteEpisode(episodeId: string): Promise<void> {
    const response = await api.delete(`${this.BASE_PATH}/${episodeId}`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete episode');
    }
  }

  // ==========================================
  // CLIP-BASED EPISODE METHODS (NEW)
  // ==========================================

  /**
   * Create a new clip-based episode from multiple articles
   */
  static async createClipEpisode(input: CreateClipEpisodeInput): Promise<CreateClipEpisodeResponse> {
    const response = await api.post<CreateClipEpisodeResponse>(`${this.BASE_PATH}/clips`, input);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create clip episode');
    }

    return response.data;
  }

  /**
   * Get all clips for an episode, ordered by position.
   * Uses collection group query on backend - no companyId needed.
   */
  static async getClips(episodeId: string): Promise<Clip[]> {
    const response = await api.get<Clip[]>(`${this.BASE_PATH}/${episodeId}/clips`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch clips');
    }

    return response.data;
  }

  /**
   * Get clip status counts for an episode.
   * Uses collection group query on backend - no companyId needed.
   */
  static async getClipStats(episodeId: string): Promise<ClipStats> {
    const response = await api.get<ClipStats>(`${this.BASE_PATH}/${episodeId}/clips/stats`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch clip stats');
    }

    return response.data;
  }

  /**
   * Update a clip (edit script, transition, content)
   */
  static async updateClip(
    episodeId: string,
    clipId: string,
    updates: UpdateClipInput
  ): Promise<Clip> {
    const response = await api.patch<Clip>(
      `/podcast/clips/${clipId}?episodeId=${episodeId}`,
      updates
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update clip');
    }

    return response.data;
  }

  /**
   * Delete a clip from an episode
   */
  static async deleteClip(episodeId: string, clipId: string): Promise<void> {
    const response = await api.delete(
      `/podcast/clips/${clipId}?episodeId=${episodeId}`
    );

    // Handle 204 No Content (success with no body) or explicit success
    // The API returns 204 for successful deletes, which may result in undefined/null response
    if (response === undefined || response === null || response.success === true) {
      return; // Delete was successful
    }

    if (response.success === false) {
      throw new Error(response.error?.message || 'Failed to delete clip');
    }
  }

  /**
   * Reorder clips in an episode
   * @param clipIds Array of clip IDs in their new order
   */
  static async reorderClips(
    episodeId: string,
    clipIds: string[]
  ): Promise<void> {
    const response = await api.post(
      `${this.BASE_PATH}/${episodeId}/clips/reorder`,
      { clipIds }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to reorder clips');
    }
  }

  // ==========================================
  // CLIP WORKFLOW METHODS
  // ==========================================

  /**
   * Trigger script generation for ALL clips in an episode
   */
  static async generateScripts(episodeId: string): Promise<{ clipCount: number; webhookResults: Array<{ clipId: string; success: boolean }> }> {
    const response = await api.post<{ message: string; episodeId: string; clipCount: number; webhookResults: Array<{ clipId: string; success: boolean }> }>(
      `${this.BASE_PATH}/${episodeId}/generate-scripts`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate scripts');
    }

    return {
      clipCount: response.data.clipCount,
      webhookResults: response.data.webhookResults,
    };
  }

  /**
   * Approve all scripts for an episode (Gate 1)
   */
  static async approveScripts(episodeId: string): Promise<{ approvedCount: number }> {
    const response = await api.post<{ message: string; episodeId: string; approvedCount: number }>(
      `${this.BASE_PATH}/${episodeId}/approve-scripts`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to approve scripts');
    }

    return { approvedCount: response.data.approvedCount };
  }

  /**
   * Trigger audio generation for ALL clips in an episode
   */
  static async generateAudio(episodeId: string): Promise<{ clipCount: number; webhookResults: Array<{ clipId: string; success: boolean }> }> {
    const response = await api.post<{ message: string; episodeId: string; clipCount: number; webhookResults: Array<{ clipId: string; success: boolean }> }>(
      `${this.BASE_PATH}/${episodeId}/generate-audio`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate audio');
    }

    return {
      clipCount: response.data.clipCount,
      webhookResults: response.data.webhookResults,
    };
  }

  /**
   * Approve all audio for an episode (Gate 2)
   */
  static async approveAudio(episodeId: string): Promise<{ approvedCount: number }> {
    const response = await api.post<{ message: string; episodeId: string; approvedCount: number }>(
      `${this.BASE_PATH}/${episodeId}/approve-audio`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to approve audio');
    }

    return { approvedCount: response.data.approvedCount };
  }

  /**
   * Merge all clips into final episode audio (legacy method - simple API call)
   */
  static async mergeEpisode(episodeId: string): Promise<void> {
    const response = await api.post(`${this.BASE_PATH}/${episodeId}/merge`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to merge episode');
    }
  }

  /**
   * Audio settings for episode merge/preview.
   * Uses the /audio endpoint which is available to all authenticated users.
   */
  static async getPodcastSettings(): Promise<PodcastAudioSettings> {
    const response = await api.get<{ podcastAudio: PodcastAudioSettings }>('/podcast/settings/audio');

    if (!response.success || !response.data?.podcastAudio) {
      return {
        introPath: '',
        outroPath: '',
        commercial1Path: '',
        commercial2Path: '',
        commercial3Path: '',
        commercial4Path: '',
        artworkPath: '',
      };
    }

    return response.data.podcastAudio;
  }

  /**
   * Update podcast audio settings (partial update)
   */
  static async updatePodcastSettings(settings: Partial<PodcastAudioSettings>): Promise<void> {
    const response = await api.put('/podcast/settings', {
      podcastAudio: settings,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update settings');
    }
  }

  /**
   * Merge episode clips with intro, outro, and interleaved commercials.
   * Builds the audio URLs array from company settings and clip audio paths,
   * then calls the generic merge endpoint.
   *
   * @param episodeId - The episode ID
   * @param clips - Array of clips with audioPath, sorted by position
   * @param audioSettings - Company podcast audio settings (intro, outro, commercials)
   * @returns The merge job ID
   */
  static async mergeEpisodeWithSettings(
    episodeId: string,
    clips: Clip[],
    audioSettings: PodcastAudioSettings
  ): Promise<{ jobId: string }> {
    const audioUrls: string[] = [];

    // 1. Add intro ONLY if path is set
    if (audioSettings.introPath && audioSettings.introPath.trim()) {
      audioUrls.push(audioSettings.introPath);
    }

    // 2. Filter commercials - only include paths that are actually set
    // Empty slots are skipped entirely, cycling only through configured ones
    const commercials = [
      audioSettings.commercial1Path,
      audioSettings.commercial2Path,
      audioSettings.commercial3Path,
      audioSettings.commercial4Path,
    ].filter((path): path is string => !!path && path.trim() !== '');

    // 3. Add clips with interleaved commercials
    clips.forEach((clip, index) => {
      // Only add clips that have audio
      if (clip.audioPath) {
        audioUrls.push(clip.audioPath);

        // Add commercial after each clip (except last) - ONLY if commercials exist
        if (index < clips.length - 1 && commercials.length > 0) {
          const commercialIndex = index % commercials.length;
          audioUrls.push(commercials[commercialIndex]);
        }
        // If no commercials configured, clips merge directly with no gap
      }
    });

    // 4. Add outro ONLY if path is set
    if (audioSettings.outroPath && audioSettings.outroPath.trim()) {
      audioUrls.push(audioSettings.outroPath);
    }

    // Call generic merge endpoint
    const response = await api.post<{ jobId: string }>('/podcast/merge', {
      audioUrls,
      outputFilename: `episode_${episodeId}_${Date.now()}.mp3`,
      metadata: { episodeId },
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to merge episode');
    }

    return response.data;
  }

  /**
   * Regenerate script for a single clip
   */
  static async regenerateClipScript(episodeId: string, clipId: string): Promise<Clip> {
    const response = await api.post<{ message: string; clip: Clip }>(
      `/podcast/clips/${clipId}/regenerate-script?episodeId=${episodeId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to regenerate clip script');
    }

    return response.data.clip;
  }

  /**
   * Regenerate audio for a single clip
   */
  static async regenerateClipAudio(episodeId: string, clipId: string): Promise<Clip> {
    const response = await api.post<{ message: string; clip: Clip }>(
      `/podcast/clips/${clipId}/regenerate-audio?episodeId=${episodeId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to regenerate clip audio');
    }

    return response.data.clip;
  }

  /**
   * Create an empty draft episode with auto-generated title
   * Automatically sets coverArtUrl from company's default artwork settings
   * @param title Optional episode title (auto-generated if not provided)
   * @param companyId Optional company ID for multi-company users (uses default company if not provided)
   */
  static async createDraftEpisode(title?: string, companyId?: string): Promise<{ id: string; title: string }> {
    // Generate default title with current date if not provided
    const defaultTitle = title || `Episode - ${new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })}`;

    // Note: Backend automatically uses company's default artwork if coverArtUrl is not provided
    const response = await api.post<{ episode: { id: string; title: string } }>(
      `${this.BASE_PATH}/draft`,
      { title: defaultTitle, companyId }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create draft episode');
    }

    return response.data.episode;
  }

  /**
   * Add clips to an existing episode
   * @param episodeId The episode ID to add clips to
   * @param articles Array of articles to create clips from
   * @param companyId Company ID for multi-company users
   */
  static async addClipsToEpisode(
    episodeId: string,
    articles: Array<{
      id: string;
      title: string;
      url: string;
      content: string;
      excerpt?: string;
      author?: string;
      publishedAt?: string;
      imageUrl?: string;
      feedLabel?: string;
    }>,
    companyId: string
  ): Promise<{ clips: Clip[] }> {
    const response = await api.post<{ message: string; clips: Clip[] }>(
      `${this.BASE_PATH}/${episodeId}/clips`,
      { articles, companyId }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to add clips to episode');
    }

    return { clips: response.data.clips };
  }

  // ==========================================
  // MULTI-COMPANY STATS
  // ==========================================

  /**
   * Get aggregated episode metrics for all companies the user belongs to.
   * Returns condensed actionable metrics per company.
   */
  static async getMultiCompanyStats(): Promise<CompanyEpisodeStats[]> {
    const response = await api.get<CompanyEpisodeStats[]>(`${this.BASE_PATH}/stats/multi`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch multi-company stats');
    }

    return response.data;
  }

  // ==========================================
  // PROCESSING EPISODES (Dashboard)
  // ==========================================

  /**
   * Processing job info for dashboard display
   */
  static readonly PROCESSING_STATUSES = [
    EpisodeStatus.GENERATING_SCRIPTS,
    EpisodeStatus.GENERATING_AUDIO,
    EpisodeStatus.MERGING,
    EpisodeStatus.PUBLISHING,
  ] as const;

  /**
   * Map episode status to human-readable job type
   */
  static getJobType(status: EpisodeStatus): string {
    switch (status) {
      case EpisodeStatus.GENERATING_SCRIPTS:
        return 'Generating scripts';
      case EpisodeStatus.GENERATING_AUDIO:
        return 'Generating audio';
      case EpisodeStatus.MERGING:
        return 'Merging clips';
      case EpisodeStatus.PUBLISHING:
        return 'Publishing';
      default:
        return 'Processing';
    }
  }

  /**
   * Calculate progress percentage for a processing episode
   */
  static getProgressPercent(episode: PodcastEpisode): number {
    const clipCount = episode.clipCount || 0;
    if (clipCount === 0) return 0;

    switch (episode.status) {
      case EpisodeStatus.GENERATING_SCRIPTS:
        return Math.round(((episode.scriptsReady || 0) / clipCount) * 100);
      case EpisodeStatus.GENERATING_AUDIO:
        return Math.round(((episode.audiosReady || 0) / clipCount) * 100);
      case EpisodeStatus.MERGING:
      case EpisodeStatus.PUBLISHING:
        // These are single operations, show as in-progress (indeterminate)
        return -1; // -1 indicates indeterminate
      default:
        return 0;
    }
  }

  /**
   * Get all episodes currently being processed.
   * Fetches episodes in generating_scripts, generating_audio, merging, and publishing states.
   */
  static async getProcessingEpisodes(): Promise<PodcastEpisode[]> {
    // Use multi-status query - single API call instead of 4 parallel calls
    const episodes = await this.getEpisodes({
      status: [...this.PROCESSING_STATUSES],
      orderBy: 'updatedAt',
      order: 'desc',
      limit: 40, // 10 per status max
    });

    return episodes;
  }

  // ==========================================
  // NEEDS ATTENTION EPISODES (Dashboard)
  // ==========================================

  /**
   * Statuses that need user attention
   */
  static readonly ATTENTION_STATUSES = [
    EpisodeStatus.FAILED,
    EpisodeStatus.SCRIPT_FAILED,
    EpisodeStatus.AUDIO_FAILED,
    EpisodeStatus.PUBLISHING_FAILED,
    EpisodeStatus.SCRIPTS_PENDING_REVIEW,
    EpisodeStatus.AUDIO_PENDING_REVIEW,
  ] as const;

  /**
   * Get human-readable issue description for attention-needing episodes
   */
  static getAttentionReason(status: EpisodeStatus): string {
    switch (status) {
      case EpisodeStatus.FAILED:
        return 'Processing failed - retry needed';
      case EpisodeStatus.SCRIPT_FAILED:
        return 'Script generation failed - retry needed';
      case EpisodeStatus.AUDIO_FAILED:
        return 'Audio generation failed - retry needed';
      case EpisodeStatus.PUBLISHING_FAILED:
        return 'Publishing failed - retry needed';
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return 'Scripts pending review';
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return 'Audio pending review';
      default:
        return 'Needs attention';
    }
  }

  /**
   * Check if a status is a failure status (vs pending review)
   */
  static isFailureStatus(status: EpisodeStatus): boolean {
    return [
      EpisodeStatus.FAILED,
      EpisodeStatus.SCRIPT_FAILED,
      EpisodeStatus.AUDIO_FAILED,
      EpisodeStatus.PUBLISHING_FAILED,
    ].includes(status);
  }

  /**
   * Get all episodes that need user attention.
   * Includes failed episodes and those pending review.
   */
  static async getAttentionEpisodes(limit: number = 10): Promise<PodcastEpisode[]> {
    // Use multi-status query - single API call instead of 6 parallel calls
    const episodes = await this.getEpisodes({
      status: [...this.ATTENTION_STATUSES],
      orderBy: 'updatedAt',
      order: 'desc',
      limit: 60, // Fetch more to allow for client-side sorting
    });

    // Sort - failures first, then by updatedAt descending
    episodes.sort((a, b) => {
      // Failures come first
      const aIsFailure = this.isFailureStatus(a.status);
      const bIsFailure = this.isFailureStatus(b.status);
      if (aIsFailure && !bIsFailure) return -1;
      if (!aIsFailure && bIsFailure) return 1;

      // Then by updatedAt descending
      const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : new Date(a.updatedAt).getTime();
      const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : new Date(b.updatedAt).getTime();
      return bTime - aTime;
    });

    return episodes.slice(0, limit);
  }

  // ==========================================
  // IN-PROGRESS EPISODES (Dashboard - Pick Up Where You Left Off)
  // ==========================================

  /**
   * Statuses for "pick up where you left off" - episodes user is actively working on
   * Excludes: processing (MO-46), attention (MO-47), published, archived
   */
  static readonly IN_PROGRESS_STATUSES = [
    EpisodeStatus.DRAFT,
    EpisodeStatus.SCRIPTS_APPROVED,
    EpisodeStatus.AUDIO_APPROVED,
    EpisodeStatus.MERGED,
  ] as const;

  /**
   * Get human-readable status label for display
   */
  static getStatusLabel(status: EpisodeStatus): string {
    switch (status) {
      case EpisodeStatus.DRAFT:
        return 'Draft';
      case EpisodeStatus.SCRIPTS_APPROVED:
        return 'Scripts Approved';
      case EpisodeStatus.AUDIO_APPROVED:
        return 'Audio Approved';
      case EpisodeStatus.MERGED:
        return 'Ready to Publish';
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return 'Scripts Ready';
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return 'Audio Ready';
      case EpisodeStatus.GENERATING_SCRIPTS:
        return 'Generating Scripts';
      case EpisodeStatus.GENERATING_AUDIO:
        return 'Generating Audio';
      case EpisodeStatus.MERGING:
        return 'Merging';
      case EpisodeStatus.PUBLISHING:
        return 'Publishing';
      case EpisodeStatus.PUBLISHED:
        return 'Published';
      case EpisodeStatus.FAILED:
        return 'Failed';
      default:
        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  }

  /**
   * Get the next action text for an episode based on its status
   */
  static getNextAction(episode: PodcastEpisode): string {
    const clipCount = episode.clipCount || 0;

    switch (episode.status) {
      case EpisodeStatus.DRAFT:
        return clipCount === 0 ? 'Add clips to episode' : 'Generate scripts';
      case EpisodeStatus.SCRIPTS_APPROVED:
        return 'Generate audio';
      case EpisodeStatus.AUDIO_APPROVED:
        return 'Merge episode';
      case EpisodeStatus.MERGED:
        return 'Publish episode';
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return `Review ${clipCount} script${clipCount !== 1 ? 's' : ''}`;
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return 'Approve and merge';
      default:
        return 'Continue editing';
    }
  }

  /**
   * Get status badge color scheme
   */
  static getStatusColor(status: EpisodeStatus): { bg: string; text: string } {
    switch (status) {
      case EpisodeStatus.DRAFT:
        return { bg: 'rgba(113, 113, 122, 0.2)', text: '#a1a1aa' }; // zinc
      case EpisodeStatus.SCRIPTS_APPROVED:
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return { bg: 'rgba(251, 191, 36, 0.2)', text: '#fbbf24' }; // amber
      case EpisodeStatus.AUDIO_APPROVED:
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return { bg: 'rgba(34, 211, 238, 0.2)', text: '#22d3ee' }; // cyan
      case EpisodeStatus.MERGED:
        return { bg: 'rgba(74, 222, 128, 0.2)', text: '#4ade80' }; // green
      case EpisodeStatus.PUBLISHED:
        return { bg: 'rgba(96, 165, 250, 0.2)', text: '#60a5fa' }; // blue
      case EpisodeStatus.FAILED:
      case EpisodeStatus.SCRIPT_FAILED:
      case EpisodeStatus.AUDIO_FAILED:
      case EpisodeStatus.PUBLISHING_FAILED:
        return { bg: 'rgba(248, 113, 113, 0.2)', text: '#f87171' }; // red
      default:
        return { bg: 'rgba(113, 113, 122, 0.2)', text: '#a1a1aa' }; // zinc
    }
  }

  /**
   * Get workflow progress percentage based on episode status.
   * Progress represents how far through the complete workflow the episode is.
   */
  static getWorkflowProgress(episode: PodcastEpisode): number {
    const clipCount = episode.clipCount || 0;

    switch (episode.status) {
      case EpisodeStatus.DRAFT:
        // Draft progress depends on whether clips have been added
        return clipCount > 0 ? 10 : 0;

      case EpisodeStatus.GENERATING_SCRIPTS:
        // Partial progress based on scriptsReady
        const scriptsProgress = clipCount > 0
          ? ((episode.scriptsReady || 0) / clipCount) * 15
          : 0;
        return Math.round(10 + scriptsProgress);

      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return 25;

      case EpisodeStatus.SCRIPTS_APPROVED:
        return 35;

      case EpisodeStatus.GENERATING_AUDIO:
        // Partial progress based on audiosReady
        const audioProgress = clipCount > 0
          ? ((episode.audiosReady || 0) / clipCount) * 25
          : 0;
        return Math.round(35 + audioProgress);

      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return 60;

      case EpisodeStatus.AUDIO_APPROVED:
        return 70;

      case EpisodeStatus.MERGING:
        return 80;

      case EpisodeStatus.MERGED:
        return 90;

      case EpisodeStatus.PUBLISHING:
        return 95;

      case EpisodeStatus.PUBLISHED:
        return 100;

      case EpisodeStatus.FAILED:
      case EpisodeStatus.SCRIPT_FAILED:
      case EpisodeStatus.AUDIO_FAILED:
      case EpisodeStatus.PUBLISHING_FAILED:
        return -1; // Indicates failed state

      default:
        return 0;
    }
  }

  /**
   * Format duration in seconds to human readable (e.g., "3:45" or "1:23:45")
   */
  static formatDuration(seconds: number | null | undefined): string {
    if (!seconds || seconds <= 0) return '—';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Format relative time (e.g., "2h", "1d", "3d")
   */
  static formatRelativeTime(date: Date | string): string {
    const now = new Date();
    const then = date instanceof Date ? date : new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${Math.max(1, diffMins)}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return `${Math.floor(diffDays / 7)}w`;
  }

  /**
   * Get episodes the user is actively working on (not processing, not needing attention).
   * These are episodes where the user needs to take the next step.
   * @param limit Maximum number of episodes to return
   * @param companyIds Optional array of company IDs to filter by (for multi-company users)
   */
  static async getInProgressEpisodes(
    limit: number = 10,
    companyIds?: string[]
  ): Promise<PodcastEpisode[]> {
    // Use multi-status query - single API call instead of 4 parallel calls
    const episodes = await this.getEpisodes({
      status: [...this.IN_PROGRESS_STATUSES],
      orderBy: 'updatedAt',
      order: 'desc',
      limit,
      companyIds,
    });

    return episodes;
  }

  // ==========================================
  // PRE-COMPUTED METRICS
  // ==========================================

  /**
   * Get pre-computed podcast metrics for the current company.
   * This is an O(1) operation - the API returns pre-computed values
   * from the podcastMetrics collection instead of computing client-side.
   */
  static async getMetrics(): Promise<{
    needsReview: number;
    inProgress: number;
    readyToPublish: number;
    published: number;
    failed: number;
  }> {
    const response = await api.get<{
      needsReview: number;
      inProgress: number;
      readyToPublish: number;
      published: number;
      failed: number;
    }>(`${this.BASE_PATH}/metrics`);

    if (!response.success || !response.data) {
      // Return zeros on failure rather than throwing
      return { needsReview: 0, inProgress: 0, readyToPublish: 0, published: 0, failed: 0 };
    }

    return response.data;
  }

  /**
   * Generate AI suggestions for episode title and description based on clips
   */
  static async generateSuggestions(episodeId: string): Promise<{
    message: string;
    episodeId: string;
    clipCount: number;
    status: string;
  }> {
    const response = await api.post<{
      message: string;
      episodeId: string;
      clipCount: number;
      status: string;
    }>(`/podcast/ai/episodes/${episodeId}/generate-suggestions`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate suggestions');
    }

    return response.data;
  }

  // ==========================================
  // CHAPTER METHODS
  // ==========================================

  /**
   * Build chapter data from clips and audio settings.
   * Chapters are created for each clip, with start times calculated
   * based on intro duration and interleaved commercials.
   *
   * Merged audio sequence: intro -> clip1 -> commercial -> clip2 -> commercial -> ... -> outro
   * Chapters point to clip start times only (no intro/outro chapters).
   */
  static buildChaptersFromClips(
    clips: Clip[],
    mergePreview: MergePreview
  ): PodcastChapter[] {
    // Sort clips by position
    const sortedClips = [...clips]
      .filter(clip => clip.audioPath && clip.duration) // Only include clips with audio
      .sort((a, b) => a.position - b.position);

    if (sortedClips.length === 0) {
      return [];
    }

    // Get intro duration (0 if not configured)
    const introDuration = mergePreview.introDuration || 0;

    // Build commercials array with durations (only configured ones)
    const commercials = [
      mergePreview.commercial1Duration,
      mergePreview.commercial2Duration,
      mergePreview.commercial3Duration,
      mergePreview.commercial4Duration,
    ].filter((dur): dur is number => dur != null && dur > 0);

    const chapters: PodcastChapter[] = [];
    let currentTime = introDuration; // Start after intro

    sortedClips.forEach((clip, index) => {
      // Add chapter for this clip
      chapters.push({
        title: clip.articleTitle,
        startTime: Math.round(currentTime), // Podbean expects integer seconds
      });

      // Advance time by clip duration
      currentTime += clip.duration || 0;

      // Add commercial duration after each clip (except last)
      if (index < sortedClips.length - 1 && commercials.length > 0) {
        const commercialIndex = index % commercials.length;
        currentTime += commercials[commercialIndex];
      }
    });

    return chapters;
  }

  // ==========================================
  // ARTWORK METHODS
  // ==========================================

  /**
   * Generate dated artwork for an episode (TEST METHOD)
   * Downloads base artwork, overlays current date, saves alongside merged audio
   */
  static async generateArtwork(episodeId: string): Promise<{ message: string; remotePath: string }> {
    const response = await api.post<{ message: string; remotePath: string }>(
      `${this.BASE_PATH}/${episodeId}/generate-artwork`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to generate artwork');
    }

    return response.data;
  }

  // ==========================================
  // PUBLISH TIME PRESETS
  // ==========================================

  /**
   * Get publish time presets from podcast settings.
   * These are the quick-select time options shown in the publish dialog.
   * Returns hours in 24hr format (0-23), e.g., [6, 7, 8, 9, 12]
   *
   * Note: This uses the /podcast/settings/audio endpoint which is available
   * to all authenticated users (not just admins).
   */
  static async getPublishTimePresets(): Promise<number[]> {
    const response = await api.get<{ publishTimePresets?: number[] }>('/podcast/settings/audio');

    // Return presets if available, otherwise return empty array (caller handles defaults)
    if (!response.success || !response.data?.publishTimePresets) {
      return [];
    }

    return response.data.publishTimePresets;
  }
}
